import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Facebook, Instagram, ChevronLeft, ChevronRight, Loader2, X, Video, MoreHorizontal,
  Clock, Pencil, Trash2, Images, FileText, Plus,
} from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./_atoms";

export function DraftsList({ items, onEdit, onDelete }) {
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const ta = a.scheduled_at || a.created_at || "";
      const tb = b.scheduled_at || b.created_at || "";
      return ta.localeCompare(tb);
    });
  }, [items]);
  if (sorted.length === 0) {
    return (
      <div className="sp-card p-12 text-center text-[color:var(--sp-text-muted)]" data-testid="publish-drafts-empty">
        <FileText className="h-8 w-8 mx-auto mb-3 text-[color:var(--sp-border-strong)]" />
        Nessuna bozza. Usa &quot;Salva bozza&quot; nel Compositore per iniziare.
      </div>
    );
  }
  return (
    <div className="sp-card overflow-hidden divide-y divide-[color:var(--sp-border)]" data-testid="publish-drafts-list">
      {sorted.map((p) => {
        const scheduled = p.status === "scheduled";
        const platforms = p.platforms || [p.platform];
        return (
          <div key={p.id} data-testid={`publish-draft-${p.id}`}
               className="p-4 flex items-center gap-3 hover:bg-[color:var(--sp-subtle)]/40 transition-colors">
            <span className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${scheduled ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
              {scheduled ? <Clock className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">
                {p.message || p.caption || "(senza testo)"}
              </div>
              <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  {platforms.includes("facebook") && <Facebook className="h-3 w-3 text-blue-600" />}
                  {platforms.includes("instagram") && <Instagram className="h-3 w-3 text-pink-600" />}
                </div>
                <StatusPill status={p.status} />
                {p.scheduled_at && (
                  <span className="inline-flex items-center gap-0.5">
                    <Clock className="h-3 w-3" /> {new Date(p.scheduled_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
                {p.media_urls?.length >= 2 && (
                  <span className="text-purple-600 inline-flex items-center gap-0.5">
                    <Images className="h-3 w-3" /> {p.media_urls.length}
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => onEdit(p)}
              data-testid={`publish-draft-edit-${p.id}`}
              className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-brand)]"
              title="Modifica">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={() => onDelete(p)}
              data-testid={`publish-draft-delete-${p.id}`}
              className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
              title="Elimina">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ==========================================================================
// Calendar — vista mese con thumb per giorno
// ==========================================================================
export function CalendarView({ onEdit, onDelete, onNewForDate }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState({ by_day: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/meta/posts/calendar?month=${month}`);
      setData(r.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    // Monday=0 layout
    const startDow = (first.getDay() + 6) % 7;
    const arr = [];
    for (let i = 0; i < startDow; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      arr.push({ day: d, iso, posts: data.by_day[iso] || [] });
    }
    return arr;
  }, [month, data]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    setSelectedDay(null);
  };

  const monthLabel = new Date(month + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const daysHeader = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

  return (
    <div className="space-y-4" data-testid="publish-calendar">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} data-testid="publish-cal-prev"
            className="p-2 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="font-display text-lg font-black capitalize min-w-[180px] text-center" data-testid="publish-cal-month">{monthLabel}</h2>
          <button onClick={() => shiftMonth(1)} data-testid="publish-cal-next"
            className="p-2 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="text-xs text-[color:var(--sp-text-muted)]" data-testid="publish-cal-total">
          {data.total} post nel mese
        </div>
      </div>

      <div className="sp-card overflow-hidden">
        <div className="grid grid-cols-7 bg-[color:var(--sp-subtle)]/40 text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">
          {daysHeader.map((d) => (
            <div key={d} className="px-2 py-2 text-center border-r border-[color:var(--sp-border)] last:border-0">{d}</div>
          ))}
        </div>
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-text-muted)]" /></div>
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((cell, i) => (
              <div key={i}
                data-testid={cell ? `publish-cal-day-${cell.iso}` : undefined}
                className={`group relative min-h-[100px] p-2 border-r border-b border-[color:var(--sp-border)] last:border-r-0 transition-colors ${cell ? "cursor-pointer hover:bg-[color:var(--sp-subtle)]/40" : "bg-slate-50"}`}
                onClick={() => { if (cell) onNewForDate(cell.iso); }}>
                {cell && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold text-[color:var(--sp-text-secondary)]">{cell.day}</div>
                      <div className="flex items-center gap-1">
                        {cell.posts.length > 0 && (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedDay(cell); }}
                            data-testid={`publish-cal-view-day-${cell.iso}`}
                            className="text-[10px] font-bold text-[color:var(--sp-brand)] hover:underline">
                            Vedi ({cell.posts.length})
                          </button>
                        )}
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 rounded-full bg-[color:var(--sp-brand)] text-white flex items-center justify-center" title="Nuovo post">
                          <Plus className="h-3 w-3" />
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {cell.posts.slice(0, 3).map((p) => {
                        const thumb = p.image_url || p.media_urls?.[0] || (p.video_url ? p.video_url : null);
                        const isVideoThumb = !p.image_url && (p.video_url || (p.media_urls?.[0] && /\.(mp4|mov|webm)$/i.test(p.media_urls[0])));
                        return (
                          <div key={p.id}
                            onClick={(e) => { e.stopPropagation(); setSelectedDay(cell); }}
                            className="flex items-center gap-1 text-[10px] rounded px-1 py-0.5 cursor-pointer hover:brightness-95 overflow-hidden"
                            style={{ background: p.status === "published" ? "#DCFCE7" : p.status === "scheduled" ? "#DBEAFE" : p.status === "error" ? "#FEE2E2" : "#F1F5F9",
                                     color: p.status === "published" ? "#166534" : p.status === "scheduled" ? "#1E40AF" : p.status === "error" ? "#991B1B" : "#475569" }}>
                            {thumb && !isVideoThumb ? (
                              <img src={thumb} alt="" className="h-4 w-4 rounded object-cover flex-shrink-0" />
                            ) : thumb && isVideoThumb ? (
                              <span className="h-4 w-4 rounded bg-black/70 text-white flex items-center justify-center flex-shrink-0"><Video className="h-2.5 w-2.5" /></span>
                            ) : (
                              <>
                                {(p.platforms || [p.platform]).includes("facebook") && <Facebook className="h-2.5 w-2.5 flex-shrink-0" />}
                                {(p.platforms || [p.platform]).includes("instagram") && <Instagram className="h-2.5 w-2.5 flex-shrink-0" />}
                              </>
                            )}
                            <span className="truncate font-bold">
                              {p.scheduled_at ? new Date(p.scheduled_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) + " " : ""}
                              {p.message || p.caption || "(media)"}
                            </span>
                          </div>
                        );
                      })}
                      {cell.posts.length > 3 && (
                        <div className="text-[10px] text-[color:var(--sp-text-muted)] font-bold">+ {cell.posts.length - 3} altri</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedDay && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setSelectedDay(null)} data-testid="publish-cal-day-modal">
          <div className="sp-card w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-black">
                {new Date(selectedDay.iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              </h3>
              <button onClick={() => setSelectedDay(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2">
              {selectedDay.posts.map((p) => {
                const thumb = p.image_url || p.media_urls?.[0];
                const isVideoThumb = !p.image_url && (p.video_url || (p.media_urls?.[0] && /\.(mp4|mov|webm)$/i.test(p.media_urls[0])));
                const isIG = (p.platforms || [p.platform]).includes("instagram");
                return (
                  <div key={p.id} className="rounded-xl border border-[color:var(--sp-border)] p-3 flex gap-3">
                    {thumb && !isVideoThumb ? (
                      <img src={thumb} alt="" className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
                    ) : isVideoThumb ? (
                      <div className="h-16 w-16 rounded-lg bg-black flex items-center justify-center flex-shrink-0 relative">
                        {p.video_url && <video src={p.video_url} className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-70" muted />}
                        <Video className="h-6 w-6 text-white z-10" />
                      </div>
                    ) : (
                      <span className="h-16 w-16 rounded-lg bg-[color:var(--sp-subtle)] flex items-center justify-center flex-shrink-0">
                        {isIG ? <Instagram className="h-6 w-6 text-pink-600" /> : <Facebook className="h-6 w-6 text-blue-600" />}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm line-clamp-3">{p.message || p.caption || "(media)"}</div>
                      <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex items-center gap-2 flex-wrap">
                        <StatusPill status={p.status} />
                        <div className="flex items-center gap-0.5">
                          {(p.platforms || [p.platform]).includes("facebook") && <Facebook className="h-3 w-3 text-blue-600" />}
                          {isIG && <Instagram className="h-3 w-3 text-pink-600" />}
                        </div>
                        {p.scheduled_at && <span>{new Date(p.scheduled_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span>}
                        {p.media_urls?.length >= 2 && (
                          <span className="text-purple-600 inline-flex items-center gap-0.5"><Images className="h-3 w-3" /> {p.media_urls.length}</span>
                        )}
                      </div>
                      {p.status === "published" && p.permalink_url && (
                        <a href={p.permalink_url} target="_blank" rel="noopener noreferrer"
                           data-testid={`publish-open-${p.id}`}
                           className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[color:var(--sp-brand)] text-white text-[11px] font-bold hover:opacity-90">
                          Apri su {isIG ? "Instagram" : "Facebook"} →
                        </a>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      {p.status !== "published" ? (
                        <>
                          <button onClick={() => { onEdit(p); setSelectedDay(null); }}
                            className="p-1.5 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-brand)]"
                            title="Modifica"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => { onDelete(p); setSelectedDay(null); }}
                            className="p-1.5 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
                            title="Elimina"><Trash2 className="h-4 w-4" /></button>
                        </>
                      ) : (
                        <button onClick={() => { onDelete({ ...p, _published: true, _isIG: isIG }); setSelectedDay(null); }}
                          data-testid={`publish-delete-published-${p.id}`}
                          className="p-1.5 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
                          title={isIG ? "Elimina (istruzioni)" : "Elimina anche dal social"}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// Uploaders — single & multi with cancel + reorder
